import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';

const DISMISSED_KEY = 'browser_support_banner_dismissed';

// Minimum supported browser versions (from ~2022)
const MIN_VERSIONS = {
  chrome: 108,   // Dec 2022
  firefox: 100,  // May 2022
  safari: 15,    // Sept 2021
  edge: 100,     // Chromium-based Edge, March 2022
};

// Detect if browser is unsupported
function isUnsupportedBrowser() {
  const ua = navigator.userAgent;

  // Internet Explorer - all versions unsupported
  if (/MSIE|Trident/.test(ua)) {
    return true;
  }

  // Old Edge (EdgeHTML, not Chromium-based) - all versions unsupported
  if (/Edge\/\d+/.test(ua) && !/Edg\//.test(ua)) {
    return true;
  }

  // Chromium-based Edge
  if (/Edg\/(\d+)/.test(ua)) {
    const match = ua.match(/Edg\/(\d+)/);
    if (match && parseInt(match[1], 10) < MIN_VERSIONS.edge) {
      return true;
    }
  }

  // Chrome (exclude Edge)
  if (/Chrome\/(\d+)/.test(ua) && !/Edg/.test(ua)) {
    const match = ua.match(/Chrome\/(\d+)/);
    if (match && parseInt(match[1], 10) < MIN_VERSIONS.chrome) {
      return true;
    }
  }

  // Firefox
  if (/Firefox\/(\d+)/.test(ua)) {
    const match = ua.match(/Firefox\/(\d+)/);
    if (match && parseInt(match[1], 10) < MIN_VERSIONS.firefox) {
      return true;
    }
  }

  // Safari (exclude Chrome/Chromium)
  if (/Safari\//.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua)) {
    const match = ua.match(/Version\/(\d+)/);
    if (match && parseInt(match[1], 10) < MIN_VERSIONS.safari) {
      return true;
    }
  }

  // Check for missing modern features as fallback
  if (
    typeof CSS === 'undefined' ||
    typeof CSS.supports !== 'function' ||
    !CSS.supports('display', 'grid') ||
    !CSS.supports('gap', '1px') ||
    typeof window.fetch === 'undefined' ||
    typeof Promise === 'undefined' ||
    typeof window.ResizeObserver === 'undefined'
  ) {
    return true;
  }

  return false;
}

export default function BrowserSupportBanner() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    try {
      if (sessionStorage.getItem(DISMISSED_KEY) === 'true') {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- initializing state from sessionStorage on mount
        setIsDismissed(true);
        return;
      }
    } catch {
      // sessionStorage not available
    }

    // In dev mode, always show the banner for testing
    const devMode = import.meta.env.VITE_DEV_MODE === 'true';

    if (devMode || isUnsupportedBrowser()) {
      setIsVisible(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    } catch {
      // sessionStorage not available
    }
  };

  if (!isVisible || isDismissed) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[200] bg-amber-500 text-amber-950">
      <div className="px-3 py-2 sm:px-6 sm:py-2.5 md:px-12 lg:px-20">
        <div className="flex items-start sm:items-center justify-between gap-2 sm:gap-3">
          <div className="flex items-start sm:items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 sm:mt-0" />
            <p className="text-xs sm:text-sm font-medium">
              {isKorean
                ? '사용 중인 브라우저 버전은 더 이상 지원되지 않습니다. 최신 버전으로 업데이트해 주세요.'
                : 'Your browser version is no longer supported. Please update to the most recent version.'}
            </p>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-amber-600 rounded transition-colors shrink-0"
            aria-label={isKorean ? '닫기' : 'Dismiss'}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
